import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    cancelObjectifRequest,
    createObjectifAutoEvaluationRequest,
    createObjectifNMoins1Request,
    fetchObjectifModificationRequestsRequest,
    fetchObjectifsNMoins1StepRequest,
    submitObjectifModificationRequest,
} from "../../../../redux/actions/performanceActions";
import { performanceApi } from "../../../../redux/api/performanceApi";
import { asArray, Field, StepState } from "../performanceUi";
import ObjectifActionModal from "../../components/ObjectifActionModal";
import ObjectifNCreationForm from "./ObjectifNCreationForm";
import "./ObjectifsNStep.css";

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("fr-FR");
};

const formatPercent = (value) => value === undefined || value === null || value === "" ? "-" : `${value}%`;
const todayInput = () => new Date().toISOString().slice(0, 10);
const requestStatusLabel = (status) => {
    if (status === "SOUMISE") return "Soumise";
    if (status === "APPROUVEE") return "Approuvée";
    if (status === "REFUSEE") return "Refusée";
    if (status === "ANNULEE") return "Annulée";
    return status || "-";
};

export default function ObjectifsNMoins1Step({ progressId, readOnly = false }) {
    const dispatch = useDispatch();
    const slice = useSelector((state) => state.performance.objectifsNMoins1);
    const data = slice.dataByProgressId[String(progressId)];
    const loading = slice.loadingByProgressId[String(progressId)];
    const error = slice.errorByProgressId[String(progressId)];
    const objectifs = asArray(data?.objectifs);
    const revisions = asArray(data?.revisions);
    const requestHistory = useSelector((state) => state.performance.modificationsObjectifs.byObjectifId);
    const [expanded, setExpanded] = useState({});
    const [historyByObjectifId, setHistoryByObjectifId] = useState({});
    const [activeModal, setActiveModal] = useState(null);
    const [evaluationForm, setEvaluationForm] = useState({ tauxRealisation: "", actionDate: todayInput(), commentaire: "" });
    const [revisionForm, setRevisionForm] = useState({});
    const [cancelReason, setCancelReason] = useState("");
    const [addOpen, setAddOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchObjectifsNMoins1StepRequest(progressId));
    }, [dispatch, progressId]);

    useEffect(() => {
        objectifs.forEach((objectif) => {
            if (objectif.id) dispatch(fetchObjectifModificationRequestsRequest(objectif.id));
        });
    }, [dispatch, objectifs]);

    useEffect(() => {
        let mounted = true;
        const loadHistories = async () => {
            const rows = await Promise.all(objectifs.map(async (objectif) => {
                try {
                    const response = await performanceApi.fetchObjectifHistorique(objectif.id);
                    return [String(objectif.id), asArray(response?.data)];
                } catch {
                    return [String(objectif.id), []];
                }
            }));
            if (mounted) setHistoryByObjectifId(Object.fromEntries(rows));
        };
        if (objectifs.length > 0) loadHistories();
        return () => { mounted = false; };
    }, [objectifs]);

    const objectiveById = useMemo(
        () => Object.fromEntries(objectifs.map((objectif) => [objectif.id, objectif])),
        [objectifs]
    );

    const latestAuto = (objectifId) => revisions.find((item) => item.objectifId === objectifId && item.typeAction === "AUTO_EVALUATION");
    const latestManager = (objectifId) => revisions.find((item) => item.objectifId === objectifId && item.typeAction === "MANAGER_EVALUATION");
    const evaluationHistory = (objectifId) => revisions.filter((item) => item.objectifId === objectifId);
    const structuralHistory = (objectifId) => asArray(historyByObjectifId[String(objectifId)]);
    const modificationRequests = (objectifId) => asArray(requestHistory[String(objectifId)]);

    const openModal = (type, objectif) => {
        if (type === "evaluation") {
            const currentRate = latestManager(objectif.id)?.tauxRealisation ?? latestAuto(objectif.id)?.tauxRealisation ?? objectif.tauxRealisation ?? "";
            setEvaluationForm({
                tauxRealisation: currentRate,
                actionDate: todayInput(),
                commentaire: "",
            });
        }
        if (type === "revision") {
            setRevisionForm({
                dateFin: objectif.dateFin || "",
                poids: objectif.poids ?? "",
                performanceCible: objectif.performanceCible ?? "",
                surperformance: objectif.surperformance ?? "",
                tolerance: objectif.tolerance ?? "",
                justification: "",
            });
        }
        if (type === "cancel") {
            setCancelReason("");
        }
        setActiveModal({ type, objectifId: objectif.id });
    };

    const closeModal = () => setActiveModal(null);

    const saveEvaluation = () => {
        const objectif = objectiveById[activeModal?.objectifId];
        if (!objectif) return;
        if (evaluationForm.tauxRealisation === "" || Number(evaluationForm.tauxRealisation) < 0 || Number(evaluationForm.tauxRealisation) > 100) {
            Swal.fire({ icon: "warning", text: "Le taux doit être compris entre 0 et 100." });
            return;
        }
        if (!String(evaluationForm.commentaire || "").trim()) {
            Swal.fire({ icon: "warning", text: "Le commentaire est obligatoire." });
            return;
        }
        dispatch(createObjectifAutoEvaluationRequest(objectif.id, {
            evaluationProgressId: Number(progressId),
            tauxRealisation: evaluationForm.tauxRealisation,
            actionDate: evaluationForm.actionDate,
            commentaire: evaluationForm.commentaire,
        }));
        closeModal();
    };

    const saveRevision = () => {
        const objectif = objectiveById[activeModal?.objectifId];
        if (!objectif) return;
        if (!String(revisionForm.justification || "").trim() || String(revisionForm.justification || "").trim().length < 10) {
            Swal.fire({ icon: "warning", text: "La justification est obligatoire et doit contenir au moins 10 caractères." });
            return;
        }
        dispatch(submitObjectifModificationRequest(objectif.id, {
            requestType: "REVISION",
            dateFin: revisionForm.dateFin,
            poids: revisionForm.poids,
            performanceCible: revisionForm.performanceCible,
            surperformance: revisionForm.surperformance,
            tolerance: revisionForm.tolerance,
            justification: revisionForm.justification,
        }, progressId, true));
        closeModal();
    };

    const saveCancel = () => {
        const objectif = objectiveById[activeModal?.objectifId];
        if (!objectif) return;
        if (String(cancelReason || "").trim().length < 5) {
            Swal.fire({ icon: "warning", text: "Le motif d'annulation doit contenir au moins 5 caractères." });
            return;
        }
        dispatch(cancelObjectifRequest(objectif.id, { commentaire: cancelReason }, progressId, true));
        closeModal();
    };

    const addObjectif = (payload) => {
        dispatch(createObjectifNMoins1Request(progressId, {
            ...payload,
            evaluationProgressId: Number(progressId),
        }));
        setAddOpen(false);
    };

    const modalObjectif = activeModal ? objectiveById[activeModal.objectifId] : null;
    const modalHistory = modalObjectif ? evaluationHistory(modalObjectif.id) : [];

    return (
        <StepState loading={loading} error={error} empty={!data}>
            {data?.blockedReason && <div className="perf-state perf-state--warning">{data.blockedReason}</div>}

            <div className="on-page">
                <div className="on-list-header">
                    <h3 className="on-list-title">Objectifs N-1</h3>
                    <button className="on-btn on-btn--primary" type="button" disabled={readOnly} onClick={() => setAddOpen(true)}>
                        + Ajouter
                    </button>
                </div>

                <div className="on-objectives-table-wrap">
                    <table className="on-objectives-table">
                        <thead>
                            <tr>
                                <th>Titre</th>
                                <th>Performance (%)</th>
                                <th>Surperformance (%)</th>
                                <th>Poids (%)</th>
                                <th>Date d'échéance</th>
                                <th>Taux réalisation</th>
                                <th>Mes révisions</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {objectifs.map((objectif) => {
                                const actions = objectif.actions || {};
                                const currentRate = latestManager(objectif.id)?.tauxRealisation ?? latestAuto(objectif.id)?.tauxRealisation ?? objectif.tauxRealisation;
                                const rows = evaluationHistory(objectif.id);
                                const isExpanded = Boolean(expanded[objectif.id]);
                                return (
                                    <React.Fragment key={objectif.id}>
                                        <tr>
                                            <td>
                                                <strong className="on-objectives-title">{objectif.titre || "-"}</strong>
                                                {objectif.description && <span className="on-objectives-description">{objectif.description}</span>}
                                            </td>
                                            <td>{formatPercent(objectif.performanceCible)}</td>
                                            <td>{formatPercent(objectif.surperformance)}</td>
                                            <td>{formatPercent(objectif.poids)}</td>
                                            <td>{formatDate(objectif.dateFin)}</td>
                                            <td>
                                                <div className="on-objectives-realisation">
                                                    <strong>{formatPercent(currentRate)}</strong>
                                                    {currentRate != null && <div className="on-progress-mini"><span style={{ width: `${Math.max(0, Math.min(Number(currentRate) || 0, 100))}%` }} /></div>}
                                                </div>
                                            </td>
                                            <td>
                                                <button className="on-revision-toggle" type="button" onClick={() => setExpanded((current) => ({ ...current, [objectif.id]: !current[objectif.id] }))}>
                                                    <span>{rows.length}</span>
                                                    <span>{isExpanded ? "▲" : "▼"}</span>
                                                </button>
                                            </td>
                                            <td>
                                                <div className="on-objectives-actions">
                                                    <button className="on-btn on-btn--outline on-btn--xs" type="button" onClick={() => openModal("details", objectif)}>Détails</button>
                                                    {actions.canRevise && <button className="on-btn on-btn--outline on-btn--xs on-btn--success" type="button" disabled={readOnly} onClick={() => openModal("revision", objectif)}>Réviser</button>}
                                                    {actions.canEvaluate && <button className="on-btn on-btn--outline on-btn--xs on-btn--success" type="button" disabled={readOnly} onClick={() => openModal("evaluation", objectif)}>Évaluer</button>}
                                                    {actions.canCancel && <button className="on-btn on-btn--outline on-btn--xs on-btn--danger" type="button" disabled={readOnly} onClick={() => openModal("cancel", objectif)}>Annuler</button>}
                                                </div>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="on-objectives-expanded-row">
                                                <td colSpan={8}>
                                                    <div className="on-objectives-row-panel">
                                                        <div className="on-request-history">
                                                            <div className="on-request-history__title">Historique des évaluations</div>
                                                            <div className="on-request-history__list">
                                                                {rows.length === 0 && <p className="on-request-history__text">Aucune évaluation enregistrée.</p>}
                                                                {rows.map((item) => (
                                                                    <div className="on-request-history__item" key={item.id}>
                                                                        <div className="on-request-history__head">
                                                                            <span className="on-request-history__type">{item.typeAction}</span>
                                                                            <strong>{formatPercent(item.tauxRealisation)}</strong>
                                                                        </div>
                                                                        <p className="on-request-history__text">{item.commentaire}</p>
                                                                        <div className="on-request-history__meta">
                                                                            {formatDate(item.actionDate)}{item.performedByNom ? ` • ${item.performedByNom}` : ""}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        <div className="on-request-history">
                                                            <div className="on-request-history__title">Historique des modifications</div>
                                                            <div className="on-request-history__list">
                                                                {modificationRequests(objectif.id).map((request) => (
                                                                    <div className="on-request-history__item" key={`req-${request.id}`}>
                                                                        <div className="on-request-history__head">
                                                                            <span className="on-request-history__type">{request.requestType || "REVISION"}</span>
                                                                            <span className={`on-status-badge on-status-badge--${String(request.status || "").toLowerCase()}`}>{requestStatusLabel(request.status)}</span>
                                                                        </div>
                                                                        <p className="on-request-history__text">{request.justification}</p>
                                                                    </div>
                                                                ))}
                                                                {structuralHistory(objectif.id).map((item) => (
                                                                    <div className="on-request-history__item" key={`hist-${item.id}`}>
                                                                        <div className="on-request-history__head">
                                                                            <span className="on-request-history__type">{item.actionType}</span>
                                                                            <span className="on-request-history__meta">{formatDate(item.createdAt)}</span>
                                                                        </div>
                                                                        {item.commentaire && <p className="on-request-history__text">{item.commentaire}</p>}
                                                                    </div>
                                                                ))}
                                                                {modificationRequests(objectif.id).length === 0 && structuralHistory(objectif.id).length === 0 && (
                                                                    <p className="on-request-history__text">Aucune modification structurelle enregistrée.</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <ObjectifActionModal
                open={activeModal?.type === "details"}
                title={modalObjectif?.titre || "Détails objectif"}
                onClose={closeModal}
                footer={<button className="on-btn on-btn--primary" type="button" onClick={closeModal}>Fermer</button>}
            >
                {modalObjectif && (
                    <div className="on-modal-grid">
                        <div className="on-facts">
                            <div className="on-fact"><span className="on-fact__key">Performance cible</span><span className="on-fact__val">{formatPercent(modalObjectif.performanceCible)}</span></div>
                            <div className="on-fact"><span className="on-fact__key">Taux actuel</span><span className="on-fact__val">{formatPercent(latestManager(modalObjectif.id)?.tauxRealisation ?? latestAuto(modalObjectif.id)?.tauxRealisation ?? modalObjectif.tauxRealisation)}</span></div>
                            <div className="on-fact"><span className="on-fact__key">Poids</span><span className="on-fact__val">{formatPercent(modalObjectif.poids)}</span></div>
                            <div className="on-fact"><span className="on-fact__key">Échéance</span><span className="on-fact__val">{formatDate(modalObjectif.dateFin)}</span></div>
                        </div>
                        <div className="on-request-history">
                            <div className="on-request-history__title">Historique des évaluations</div>
                            <div className="on-request-history__list">
                                {modalHistory.map((item) => (
                                    <div className="on-request-history__item" key={item.id}>
                                        <div className="on-request-history__head">
                                            <span className="on-request-history__type">{item.typeAction}</span>
                                            <strong>{formatPercent(item.tauxRealisation)}</strong>
                                        </div>
                                        <p className="on-request-history__text">{item.commentaire}</p>
                                        <div className="on-request-history__meta">{formatDate(item.actionDate)}{item.performedByNom ? ` • ${item.performedByNom}` : ""}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </ObjectifActionModal>

            <ObjectifActionModal
                open={activeModal?.type === "evaluation"}
                title="Évaluer l'objectif N-1"
                onClose={closeModal}
                footer={
                    <>
                        <button className="on-btn on-btn--outline" type="button" onClick={closeModal}>Annuler</button>
                        <button className="on-btn on-btn--primary" type="button" onClick={saveEvaluation}>Enregistrer</button>
                    </>
                }
            >
                {modalObjectif && (
                    <>
                        <div className="on-form on-form--modal">
                            <div className="on-form__section-label">Informations de l'objectif</div>
                            <div className="on-form__row on-form__row--2">
                                <Field label="Performance cible"><input className="on-input" value={formatPercent(modalObjectif.performanceCible)} disabled /></Field>
                                <Field label="Taux actuel"><input className="on-input" value={formatPercent(latestManager(modalObjectif.id)?.tauxRealisation ?? latestAuto(modalObjectif.id)?.tauxRealisation ?? modalObjectif.tauxRealisation)} disabled /></Field>
                            </div>
                            <div className="on-form__row on-form__row--3">
                                <Field label="Nouveau taux de réalisation (%)"><input className="on-input" type="number" min="0" max="100" value={evaluationForm.tauxRealisation} onChange={(event) => setEvaluationForm((current) => ({ ...current, tauxRealisation: event.target.value }))} /></Field>
                                <Field label="Date d'évaluation"><input className="on-input" type="date" value={evaluationForm.actionDate} onChange={(event) => setEvaluationForm((current) => ({ ...current, actionDate: event.target.value }))} /></Field>
                                <Field label="Commentaire"><textarea className="on-textarea" rows={3} value={evaluationForm.commentaire} onChange={(event) => setEvaluationForm((current) => ({ ...current, commentaire: event.target.value }))} /></Field>
                            </div>
                        </div>
                        <div className="on-request-history">
                            <div className="on-request-history__title">Historique des évaluations</div>
                            <div className="on-request-history__list">
                                {modalHistory.map((item) => (
                                    <div className="on-request-history__item" key={item.id}>
                                        <div className="on-request-history__head">
                                            <span className="on-request-history__type">{item.typeAction}</span>
                                            <strong>{formatPercent(item.tauxRealisation)}</strong>
                                        </div>
                                        <p className="on-request-history__text">{item.commentaire}</p>
                                        <div className="on-request-history__meta">{formatDate(item.actionDate)}{item.performedByNom ? ` • ${item.performedByNom}` : ""}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </ObjectifActionModal>

            <ObjectifActionModal
                open={activeModal?.type === "revision"}
                title="Réviser l'objectif N-1"
                onClose={closeModal}
                footer={
                    <>
                        <button className="on-btn on-btn--outline" type="button" onClick={closeModal}>Annuler</button>
                        <button className="on-btn on-btn--primary" type="button" onClick={saveRevision}>Enregistrer</button>
                    </>
                }
            >
                <div className="on-form on-form--modal">
                    <div className="on-form__row on-form__row--2">
                        <Field label="Date d'échéance"><input className="on-input" type="date" value={revisionForm.dateFin || ""} onChange={(event) => setRevisionForm((current) => ({ ...current, dateFin: event.target.value }))} /></Field>
                        <Field label="Poids (%)"><input className="on-input" type="number" min="0" max="100" value={revisionForm.poids || ""} onChange={(event) => setRevisionForm((current) => ({ ...current, poids: event.target.value }))} /></Field>
                    </div>
                    <div className="on-form__row on-form__row--3">
                        <Field label="Performance cible (%)"><input className="on-input" type="number" min="0" max="100" value={revisionForm.performanceCible || ""} onChange={(event) => setRevisionForm((current) => ({ ...current, performanceCible: event.target.value }))} /></Field>
                        <Field label="Surperformance (%)"><input className="on-input" type="number" min="0" max="100" value={revisionForm.surperformance || ""} onChange={(event) => setRevisionForm((current) => ({ ...current, surperformance: event.target.value }))} /></Field>
                        <Field label="Tolérance (%)"><input className="on-input" type="number" min="0" max="100" value={revisionForm.tolerance || ""} onChange={(event) => setRevisionForm((current) => ({ ...current, tolerance: event.target.value }))} /></Field>
                    </div>
                    <Field label="Justification"><textarea className="on-textarea" rows={4} value={revisionForm.justification || ""} onChange={(event) => setRevisionForm((current) => ({ ...current, justification: event.target.value }))} /></Field>
                </div>
            </ObjectifActionModal>

            <ObjectifActionModal
                open={activeModal?.type === "cancel"}
                title="Annuler l'objectif"
                onClose={closeModal}
                footer={
                    <>
                        <button className="on-btn on-btn--outline" type="button" onClick={closeModal}>Retour</button>
                        <button className="on-btn on-btn--danger" type="button" onClick={saveCancel}>Confirmer</button>
                    </>
                }
            >
                <Field label="Motif d'annulation">
                    <textarea className="on-textarea" rows={4} value={cancelReason} onChange={(event) => setCancelReason(event.target.value)} />
                </Field>
            </ObjectifActionModal>

            <ObjectifActionModal
                open={addOpen}
                title="Nouvel objectif N-1"
                onClose={() => setAddOpen(false)}
            >
                <ObjectifNCreationForm onSubmit={addObjectif} onCancel={() => setAddOpen(false)} submitLabel="Ajouter l'objectif N-1" />
            </ObjectifActionModal>
        </StepState>
    );
}
